// ConferenceContext: Manages LiveKit conference state at the app root level.
// Mounted above the route-scoped providers (see MainLayoutWithRouter) so the room
// survives channel switches, cross-workspace navigation, and route hydration blips.
// Route identity (workspaceId/profileId) is captured at join time, not read from
// route context — the provider must not depend on any route-scoped ancestor.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useLiveKitRoom } from '@livekit/components-react';
import { post } from '@rails/request.js';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useLiveQuery } from 'dexie-react-hooks';
import { Track, VideoPresets } from 'livekit-client';

import { apiRoute } from '../lib/api';
import db, { DEXIE_IS_LOADING } from '../lib/db';
import { logError } from '../lib/logger';

// Exported so the device-preference hook writes to the same namespace this reads from —
// the two had already drifted on how they spell a missing profile.
export const buildLocalStorageKey = (profileId, key) => `output:media-device:${profileId || 'anonymous'}:${key}`;

const fetchCredentials = async (channelId) => {
  try {
    const response = await post(apiRoute(`/v1/channels/${channelId}/livekit`));
    const data = await response.json;
    if (data.ok) {
      return data.data.livekit;
    } else {
      throw new Error((Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Failed to get credentials');
    }
  } catch (err) {
    throw new Error(err.message || 'Failed to get credentials');
  }
};

const ConferenceContext = createContext(null);

export function ConferenceProvider({ children }) {
  const [status, setStatus] = useState('idle'); // idle | joining | joined | error
  const [credentials, setCredentials] = useState(null);
  // where the active call lives — captured at join time so the provider works above route context
  const [session, setSession] = useState(null); // { workspaceId, profileId } | null
  const joiningRef = useRef(false); // synchronous guard against duplicate joins

  const profileId = session?.profileId || null;
  const workspaceId = session?.workspaceId || null;

  const [microphoneIsMuted, setMicrophoneIsMuted] = useLocalStorage(buildLocalStorageKey(profileId, 'toggle:microphone'), false);
  const [cameraIsMuted, setCameraIsMuted] = useLocalStorage(buildLocalStorageKey(profileId, 'toggle:camera'), false);

  // load connected channel
  const connectedChannel = useLiveQuery(
    () => {
      if (!credentials?.channelId || !workspaceId) return null;
      return db.channels.get([workspaceId, String(credentials.channelId)]);
    },
    [credentials?.channelId, workspaceId],
    DEXIE_IS_LOADING,
  );

  const connectedChannelIsLoaded = useMemo(() => {
    if (!connectedChannel) return false;
    if (connectedChannel === DEXIE_IS_LOADING) return false;
    if (!credentials?.url || !credentials?.token) return false;
    return true;
  }, [credentials, connectedChannel]);

  // leave handler
  const handleLeave = useCallback(() => {
    window.dispatchEvent(new CustomEvent('output:internal:sfx:one-shot', { detail: { clip: 'conference-ended' } }));
    joiningRef.current = false;
    setStatus('idle');
    setCredentials(null);
  }, []);

  // use hook unconditionally, but only connect when we have credentials
  const shouldConnect = status === 'joined' && connectedChannelIsLoaded;
  const { room, htmlProps: liveKitRoomProps } = useLiveKitRoom({
    video: !cameraIsMuted,
    audio: !microphoneIsMuted,
    token: credentials?.token,
    serverUrl: credentials?.url,
    connect: shouldConnect,
    onDisconnected: handleLeave,
    roomOptions: {
      // automatically manage subscribed video quality
      adaptiveStream: { pixelDensity: 1, pauseVideoInBackground: false },
      // optimize publishing bandwidth and CPU for published tracks
      dynacast: true,
      // default capture settings - use lower resolution for smaller bubbles
      videoCaptureDefaults: {
        resolution: VideoPresets.h540.resolution,
      },
    },
  });

  // join handler — callers pass the route identity of the channel they're joining so the
  // mute-preference keys and channel lookup don't depend on which route is mounted later
  const handleJoin = useCallback(
    async (channelId, { workspaceId: joinWorkspaceId = null, profileId: joinProfileId = null } = {}) => {
      if (!channelId) throw new Error('No channelId provided');

      // synchronous guard: prevent duplicate joins even with rapid successive calls
      if (joiningRef.current || status === 'joined') throw new Error('Already on a conference call');
      joiningRef.current = true;

      // capture identity before the credentials round-trip so the localStorage-backed
      // mute preferences settle on the joining profile's keys before the room connects
      setSession({ workspaceId: joinWorkspaceId, profileId: joinProfileId });

      // mark as joining and fetch credentials
      setStatus('joining');
      try {
        const creds = await fetchCredentials(channelId);
        setCredentials({ ...creds, channelId });
        setStatus('joined');
      } catch (error) {
        joiningRef.current = false;
        setStatus('error');
        logError('ConferenceContext', 'Failed to get credentials', { error });
        throw error;
      }
    },
    [status],
  );

  // toggle handlers
  const handleToggleCamera = useCallback(async () => {
    const oldValue = cameraIsMuted;
    const newValue = !oldValue;
    setCameraIsMuted(newValue);

    // update room track if connected, revert on error
    if (room?.localParticipant) {
      try {
        await room.localParticipant.setCameraEnabled(!newValue);
      } catch (error) {
        logError('ConferenceContext', 'Failed to toggle camera', { error });
        setCameraIsMuted(oldValue);
      }
    }
  }, [room, cameraIsMuted, setCameraIsMuted]);

  const handleToggleMicrophone = useCallback(async () => {
    const oldValue = microphoneIsMuted;
    const newValue = !oldValue;
    setMicrophoneIsMuted(newValue);

    // update room track if connected, revert on error
    if (room?.localParticipant) {
      try {
        await room.localParticipant.setMicrophoneEnabled(!newValue);
      } catch (error) {
        logError('ConferenceContext', 'Failed to toggle microphone', { error });
        setMicrophoneIsMuted(oldValue);
      }
    }
  }, [room, microphoneIsMuted, setMicrophoneIsMuted]);

  const handleToggleScreenShare = useCallback(async () => {
    if (!room?.localParticipant) return;

    // check if local participant currently has a screen share track
    const sharing = Array.from(room.localParticipant.trackPublications.values()).some(
      (pub) => pub.source === Track.Source.ScreenShare && pub.track,
    );

    // update room track if connected
    try {
      await room.localParticipant.setScreenShareEnabled(!sharing);
    } catch (error) {
      logError('ConferenceContext', 'Failed to toggle screen share', { error });
    }
  }, [room]);

  // event listener for join conference
  const handleOutputJoinConference = useCallback(
    async (event) => {
      const { channelId, workspaceId: eventWorkspaceId, profileId: eventProfileId } = event.detail || {};
      try {
        await handleJoin(channelId, { workspaceId: eventWorkspaceId, profileId: eventProfileId });
      } catch (error) {
        logError('ConferenceContext', 'Failed to join conference via event', { error, channelId });
      }
    },
    [handleJoin],
  );

  // event listener for leave conference
  const handleOutputLeaveConference = useCallback(() => {
    handleLeave();
  }, [handleLeave]);

  // attach global window handlers
  useEffect(() => {
    window.addEventListener('output:join-conference', handleOutputJoinConference);
    window.addEventListener('output:leave-conference', handleOutputLeaveConference);

    return () => {
      window.removeEventListener('output:join-conference', handleOutputJoinConference);
      window.removeEventListener('output:leave-conference', handleOutputLeaveConference);
    };
  }, [handleOutputJoinConference, handleOutputLeaveConference]);

  const value = useMemo(
    () => ({
      // state
      status,
      credentials,
      room,
      liveKitRoomProps,
      connectedChannel,
      connectedChannelIsLoaded,
      microphoneIsMuted,
      cameraIsMuted,
      // identity of the active call's workspace/profile — in-call UI must use these
      // instead of route context, which can point at a different workspace mid-call
      workspaceId,
      profileId,

      // actions
      joinConference: handleJoin,
      leaveConference: handleLeave,
      toggleCamera: handleToggleCamera,
      toggleMicrophone: handleToggleMicrophone,
      toggleScreenShare: handleToggleScreenShare,
    }),
    [
      status,
      credentials,
      room,
      liveKitRoomProps,
      connectedChannel,
      connectedChannelIsLoaded,
      microphoneIsMuted,
      cameraIsMuted,
      workspaceId,
      profileId,
      handleJoin,
      handleLeave,
      handleToggleCamera,
      handleToggleMicrophone,
      handleToggleScreenShare,
    ],
  );

  return <ConferenceContext.Provider value={value}>{children}</ConferenceContext.Provider>;
}

export function useConference() {
  const context = useContext(ConferenceContext);
  if (!context) {
    throw new Error('useConference must be used within ConferenceProvider');
  }
  return context;
}
